import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { cn } from "../lib/utils";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { PaginationControls } from "../components/Pagination";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Separator } from "../components/ui/separator";
import { Card, CardHeader, CardTitle, CardContent } from "../components/ui/card";
import DateFilter from "../components/DateFilter";
import BehavioralSpamView from "./BehavioralSpamView";
import {
  Search,
  RefreshCw,
  Phone,
  CheckCircle,
  AlertTriangle,
  Ban,
  Clock,
  ExternalLink,
  Skull,
  Globe,
  Upload,
  Copy,
  FileText,
  MessageSquare,
  Target,
  User,
  Calendar,
  Building,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Filter,
  X,
  MessageCircleOff,
  Play,
  Pause,
  Volume2,
  VolumeX,
  Download,
  FileSpreadsheet,
  Bot,
  ShoppingCart,
  ShieldCheck,
  SkipForward,
  ShieldAlert,
  Users,
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Get today's date range in EST (matching DateFilter format)
const getTodayEST = () => {
  const now = new Date();
  const estTime = new Date(now.toLocaleString("en-US", { timeZone: "America/New_York" }));
  
  const year = estTime.getFullYear();
  const month = String(estTime.getMonth() + 1).padStart(2, '0');
  const day = String(estTime.getDate()).padStart(2, '0');
  
  return {
    startDate: `${year}-${month}-${day}T00:00:00`,
    endDate: `${year}-${month}-${day}T23:59:59`,
    preset: "today",
    timezone: "America/New_York",
  };
};

// Format timestamp for display (already stored in EST)
const formatToEST = (dateStr) => {
  if (!dateStr) return "N/A";
  try {
    // Timestamp is already in EST format (YYYY-MM-DD HH:MM:SS)
    // Just format it nicely for display
    let normalizedDateStr = dateStr;
    if (!dateStr.includes('T')) {
      // Format like "2026-03-19 00:12:17" - treat as local EST time
      normalizedDateStr = dateStr.replace(' ', 'T');
    }
    const date = new Date(normalizedDateStr);
    // Format without timezone conversion since it's already EST
    const month = date.toLocaleString("en-US", { month: "short" });
    const day = date.getDate();
    const hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const hour12 = hours % 12 || 12;
    return `${month} ${day}, ${hour12.toString().padStart(2, '0')}:${minutes} ${ampm} EST`;
  } catch {
    return dateStr;
  }
};

const formatDuration = (seconds) => {
  if (!seconds) return "0:00";
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

const categoryConfig = {
  valid_service_call: { label: "Valid", color: "bg-emerald-500/10 text-emerald-500 border-emerald-500/20" },
  fake_call: { label: "Fake", color: "bg-red-500/10 text-red-500 border-red-500/20" },
  spam_call: { label: "Fake", color: "bg-red-500/10 text-red-500 border-red-500/20" },
  adult_call: { label: "Adult", color: "bg-purple-500/10 text-purple-500 border-purple-500/20" },
  ivr_call: { label: "IVR", color: "bg-blue-500/10 text-blue-500 border-blue-500/20" },
  non_us_accent: { label: "Non-US", color: "bg-orange-500/10 text-orange-500 border-orange-500/20" },
  buy_sell_intent: { label: "Buy/Sell", color: "bg-cyan-500/10 text-cyan-500 border-cyan-500/20" },
  blacklisted_product: { label: "Blacklisted", color: "bg-rose-500/10 text-rose-400 border-rose-500/20" },
  skipped_call: { label: "Skipped", color: "bg-slate-500/10 text-slate-400 border-slate-500/20" },
  dead_call: { label: "Dead", color: "bg-slate-500/10 text-slate-500 border-slate-500/20" },
  pending: { label: "Pending", color: "bg-yellow-500/10 text-yellow-500 border-yellow-500/20" },
};

const getCategoryIcon = (category) => {
  switch (category) {
    case "valid_service_call": return <CheckCircle className="h-4 w-4" />;
    case "fake_call":
    case "spam_call": return <AlertTriangle className="h-4 w-4" />;
    case "adult_call": return <Ban className="h-4 w-4" />;
    case "dead_call": return <Skull className="h-4 w-4" />;
    case "ivr_call": return <Bot className="h-4 w-4" />;
    case "non_us_accent": return <Globe className="h-4 w-4" />;
    case "buy_sell_intent": return <ShoppingCart className="h-4 w-4" />;
    case "pending": return <Clock className="h-4 w-4" />;
    default: return <Phone className="h-4 w-4" />;
  }
};

const getConfidenceScoreColor = (score) => {
  if (score >= 80) return "text-emerald-500";
  if (score >= 60) return "text-blue-500";
  if (score >= 40) return "text-yellow-500";
  return "text-red-500";
};

// Copy button component
const CopyButton = ({ text, label }) => {
  const handleCopy = () => {
    if (text) {
      navigator.clipboard.writeText(text);
      toast.success("Copied to clipboard.");
    }
  };
  
  return (
    <Button
      variant="ghost"
      size="sm"
      className="h-7 px-2 text-muted-foreground hover:text-foreground"
      onClick={handleCopy}
    >
      <Copy className="h-3.5 w-3.5" />
    </Button>
  );
};

// Audio Player component
const AudioPlayer = ({ recordingUrl }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const audioRef = useRef(null);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleTimeUpdate = () => setCurrentTime(audio.currentTime);
    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
      setIsLoading(false);
    };
    const handleEnded = () => setIsPlaying(false);
    const handleError = () => {
      setError("Unable to load audio");
      setIsLoading(false);
    };
    const handleLoadStart = () => setIsLoading(true);

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);
    audio.addEventListener('loadstart', handleLoadStart);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
      audio.removeEventListener('loadstart', handleLoadStart);
    };
  }, [recordingUrl]);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    
    if (isPlaying) {
      audio.pause();
    } else {
      audio.play();
    }
    setIsPlaying(!isPlaying);
  };

  const toggleMute = () => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.muted = !isMuted;
    setIsMuted(!isMuted);
  };

  const handleSeek = (e) => {
    const audio = audioRef.current;
    if (!audio) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    audio.currentTime = percent * duration;
  };

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!recordingUrl) {
    return (
      <div className="flex items-center justify-center p-4 bg-muted/30 rounded-lg">
        <p className="text-sm text-muted-foreground">No recording available</p>
      </div>
    );
  }

  return (
    <div className="bg-muted/30 rounded-lg p-4">
      <audio ref={audioRef} src={recordingUrl} preload="metadata" />
      
      {error ? (
        <div className="flex items-center justify-center p-2">
          <p className="text-sm text-red-500">{error}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {/* Progress bar */}
          <div 
            className="h-2 bg-muted rounded-full cursor-pointer overflow-hidden"
            onClick={handleSeek}
          >
            <div 
              className="h-full bg-blue-500 rounded-full transition-all"
              style={{ width: `${duration ? (currentTime / duration) * 100 : 0}%` }}
            />
          </div>
          
          {/* Controls */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="icon"
                className="h-9 w-9"
                onClick={togglePlay}
                disabled={isLoading}
              >
                {isLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : isPlaying ? (
                  <Pause className="h-4 w-4" />
                ) : (
                  <Play className="h-4 w-4" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-9 w-9"
                onClick={toggleMute}
              >
                {isMuted ? (
                  <VolumeX className="h-4 w-4" />
                ) : (
                  <Volume2 className="h-4 w-4" />
                )}
              </Button>
            </div>
            
            <div className="text-sm text-muted-foreground font-mono">
              {formatTime(currentTime)} / {formatTime(duration)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// useState import needed for AudioPlayer
// (already imported at top of file)

export default function CallAnalysisPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState(searchParams.get('category') || "all");
  const [categoryCounts, setCategoryCounts] = useState({});
  const [publisherFilter, setPublisherFilter] = useState(searchParams.get('publisher') || "all");
  const [manualValidFilter, setManualValidFilter] = useState("all");
  const [publishers, setPublishers] = useState([]);
  // Seed date filter from URL if the caller (e.g., Dashboard row click) passed
  // start_date / end_date — keeps the DateFilter picker + downstream views in
  // sync with the Global Date Filter chosen upstream. Fallback = today (EST).
  const [dateFilter, setDateFilter] = useState(() => {
    const urlStart = searchParams.get("start_date");
    const urlEnd = searchParams.get("end_date");
    if (urlStart && urlEnd) {
      return {
        startDate: urlStart,
        endDate: urlEnd,
        preset: "custom",
        timezone: "America/New_York",
      };
    }
    return getTodayEST();
  });
  const [reanalyzing, setReanalyzing] = useState(null);
  const [blockingCaller, setBlockingCaller] = useState(null);
  const [selectedCall, setSelectedCall] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const fileInputRef = useRef(null);

  // Count active filters
  const activeFilters = [
    categoryFilter !== "all" ? 1 : 0,
    publisherFilter !== "all" ? 1 : 0,
    manualValidFilter !== "all" ? 1 : 0,
    search ? 1 : 0,
  ].reduce((a, b) => a + b, 0);

  useEffect(() => {
    fetchPublishers();
  }, []);

  // Debounce search input (400ms)
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    // Update URL params when filters change
    const params = new URLSearchParams();
    if (categoryFilter !== "all") params.set("category", categoryFilter);
    if (publisherFilter !== "all") params.set("publisher", publisherFilter);
    setSearchParams(params);
  }, [categoryFilter, publisherFilter, setSearchParams]);

  useEffect(() => {
    setCurrentPage(1);
    fetchCalls();
    fetchCategoryCounts();
  }, [debouncedSearch, categoryFilter, publisherFilter, manualValidFilter, dateFilter]);

  const fetchPublishers = async () => {
    try {
      const response = await axios.get(`${API}/publishers/names`);
      setPublishers(response.data.publishers || []);
    } catch (error) {
      console.error("Failed to fetch publishers:", error);
    }
  };

  const fetchCalls = async () => {
    // Behavioral Spam pill renders a completely different (caller-grouped)
    // view; skip the calls-endpoint round-trip while it's active.
    if (categoryFilter === "behavioral_spam") {
      setCalls([]);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (debouncedSearch) params.append("search", debouncedSearch);
      if (categoryFilter !== "all") params.append("category", categoryFilter);
      if (publisherFilter !== "all") params.append("publisher", publisherFilter);
      if (manualValidFilter !== "all") params.append("manual_valid", manualValidFilter);
      // When searching, skip date filter to search across ALL dates
      if (dateFilter && !debouncedSearch) {
        params.append("start_date", dateFilter.startDate);
        params.append("end_date", dateFilter.endDate);
      }
      
      const response = await axios.get(`${API}/calls?limit=1000&${params}`);
      setCalls(response.data);
    } catch (error) {
      console.error("Failed to fetch calls:", error);
      toast.error("Failed to load calls");
    } finally {
      setLoading(false);
    }
  };

  // Fetch counts for pill badges. Uses the same filters as the calls list
  // (except category itself) so badge numbers always match what the tab would show.
  const fetchCategoryCounts = async () => {
    try {
      const params = new URLSearchParams();
      if (debouncedSearch) params.append("search", debouncedSearch);
      if (publisherFilter !== "all") params.append("publisher", publisherFilter);
      if (manualValidFilter !== "all") params.append("manual_valid", manualValidFilter);
      if (dateFilter && !debouncedSearch) {
        params.append("start_date", dateFilter.startDate);
        params.append("end_date", dateFilter.endDate);
      }
      const res = await axios.get(`${API}/calls/category-counts?${params}`);
      const raw = res.data?.category_counts || {};
      setCategoryCounts({
        ...raw,
        all: res.data?.total ?? 0,
        fake: res.data?.fake ?? 0,
      });
    } catch (e) {
      // Non-fatal — badges just show 0 in that case
      setCategoryCounts({});
    }
  };

  const handleDateFilterChange = (filter) => {
    if (filter) {
      setDateFilter(filter);
    } else {
      setDateFilter(getTodayEST());
    }
  };

  const handleReanalyze = async (callId) => {
    setReanalyzing(callId);
    try {
      await axios.post(`${API}/calls/${callId}/reanalyze`);
      toast.success("Call analysis started");
      setTimeout(() => {
        fetchCalls();
        setReanalyzing(null);
      }, 3000);
    } catch (error) {
      console.error("Failed to reanalyze:", error);
      toast.error("Failed to start analysis");
      setReanalyzing(null);
    }
  };

  const toggleManualValid = async (callId, currentVal) => {
    try {
      const res = await axios.post(`${API}/calls/${callId}/manual-valid`);
      const { manual_valid: newVal, category: newCategory } = res.data;
      // Update the call in local state with new category
      setCalls(prev => prev.filter(c => {
        if (c.id !== callId) return true;
        // If we're on a filtered view and category changed, remove from list
        if (categoryFilter !== "all" && newCategory === "valid_service_call" && newVal) {
          return false;
        }
        return true;
      }).map(c => c.id === callId ? { ...c, manual_valid: newVal, category: newCategory } : c));
      toast.success(newVal ? "Marked as valid — moved to Valid Calls" : "Valid mark removed — restored original category");
    } catch (error) {
      console.error("Failed to toggle manual valid:", error);
      toast.error("Failed to update");
    }
  };

  const handleBlockCaller = async (callerNumber) => {
    if (!callerNumber) {
      toast.error("No caller number on this call");
      return;
    }
    setBlockingCaller(callerNumber);
    try {
      const res = await axios.post(`${API}/callers/${encodeURIComponent(callerNumber)}/block`);
      const { ringba_sync } = res.data;
      // Update local state so any other rows for this caller reflect the block
      setCalls(prev => prev.map(c => (
        c.caller_number === callerNumber
          ? { ...c, caller_blocked: true, caller_status: "blocked" }
          : c
      )));
      if (ringba_sync === "success") {
        toast.success(`Caller ${callerNumber} blocked and synced to Ringba`);
      } else if (ringba_sync === "queued") {
        toast.success(`Caller ${callerNumber} blocked locally — Ringba sync will retry in background`);
      } else if (ringba_sync === "failed") {
        toast.warning(`Caller ${callerNumber} blocked locally — Ringba sync failed, will auto-retry`);
      } else {
        toast.success(`Caller ${callerNumber} blocked`);
      }
    } catch (error) {
      console.error("Failed to block caller:", error);
      toast.error(error?.response?.data?.detail || "Failed to block caller");
    } finally {
      setBlockingCaller(null);
    }
  };


  const openCallDetail = (call) => {
    setSelectedCall(call);
    setModalOpen(true);
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      'text/csv',
    ];
    
    if (!validTypes.includes(file.type) && !file.name.match(/\.(xlsx|xls|csv)$/i)) {
      toast.error("Please upload an Excel (.xlsx) or CSV file");
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/calls/upload-excel`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      
      toast.success(`Uploaded ${response.data.imported || 0} calls successfully`);
      fetchCalls();
    } catch (error) {
      console.error("Upload failed:", error);
      toast.error(error.response?.data?.detail || "Failed to upload file");
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const clearAllFilters = () => {
    setSearch("");
    setCategoryFilter("all");
    setPublisherFilter("all");
    setManualValidFilter("all");
  };

  // Export to CSV function
  const exportToCSV = () => {
    if (calls.length === 0) {
      toast.error("No data to export");
      return;
    }

    const headers = [
      "Caller ID",
      "Publisher Name",
      "Offer Name",
      "Buyer",
      "Call Duration (sec)",
      "Call Timestamp",
      "Classification",
      "Confidence Score",
      "Summary",
      "Keywords"
    ];

    const rows = calls.map(call => [
      call.caller_number || "",
      call.publisher_name || "",
      call.offer_name || call.campaign_name || "",
      call.buyer_name || "",
      call.call_duration || 0,
      call.call_timestamp || "",
      categoryConfig[call.category]?.label || call.category || "Pending",
      call.confidence_score || 0,
      (call.summary || "").replace(/"/g, '""'),
      (call.keywords || []).join("; ")
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `call_analysis_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success(`Exported ${calls.length} calls to CSV`);
  };

  // Export single call report
  const exportCallReport = (call) => {
    const report = `
CALL ANALYSIS REPORT
====================
Generated: ${new Date().toLocaleString()}

CALL INFORMATION
----------------
Caller ID: ${call.caller_number || "N/A"}
Publisher: ${call.publisher_name || "N/A"}
Offer: ${call.offer_name || call.campaign_name || "N/A"}
Buyer: ${call.buyer_name || "None"}
DID Number: ${call.did_number || call.inbound_number || "N/A"}
Date & Time: ${call.call_timestamp || "N/A"}
Duration: ${formatDuration(call.call_duration)}

AI ANALYSIS
-----------
Classification: ${categoryConfig[call.category]?.label || call.category || "Pending"}
Confidence Score: ${call.confidence_score || 0}%
Keywords: ${(call.keywords || []).join(", ") || "None detected"}

SUMMARY
-------
${call.summary || "No summary available"}

TRANSCRIPT
----------
${call.transcript || "No transcript available"}
    `.trim();

    const blob = new Blob([report], { type: "text/plain;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `call_report_${call.caller_number || call.id}_${new Date().toISOString().split('T')[0]}.txt`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success("Report downloaded");
  };

  const getCategoryLabel = (cat) => {
    const labels = {
      all: "All Categories",
      valid_service_call: "Valid Calls",
      spam_call: "Fake Calls",
      adult_call: "Adult Calls",
      dead_call: "Dead Calls",
      non_us_accent: "Non-US Accent",
      pending: "Pending",
    };
    return labels[cat] || cat;
  };

  // Pagination logic
  const totalPages = Math.max(1, Math.ceil(calls.length / 50));
  const paginatedCalls = calls.slice((currentPage - 1) * 50, currentPage * 50);

  return (
    <div className="space-y-6" data-testid="call-analysis-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold text-white tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {publisherFilter !== "all" ? (
              <>Call Analysis: <span className="text-blue-400">{publisherFilter}</span></>
            ) : (
              "Call Analysis"
            )}
          </h1>
          <p className="text-xs sm:text-sm text-slate-500 mt-1">
            {calls.length} calls found{calls.length > 50 ? ` (page ${currentPage} of ${totalPages})` : ""}
          </p>
        </div>
        <div className="flex items-center gap-2.5">
          <Badge variant="outline" className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20 text-[11px] font-semibold tracking-wide">
            EST Timezone
          </Badge>
          <div className="relative">
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={handleFileUpload}
              className="hidden"
              id="file-upload"
            />
            <Button
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              data-testid="upload-btn"
              className="bg-blue-600 hover:bg-blue-500 text-white shadow-[0_0_15px_rgba(37,99,235,0.15)] transition-all"
            >
              {uploading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Upload className="h-4 w-4 mr-2" />
              )}
              Upload Data
            </Button>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={exportToCSV}
            disabled={calls.length === 0}
            data-testid="export-csv-btn"
            className="bg-[#11141D] hover:bg-white/5 border-white/10 text-slate-200 transition-all"
          >
            <FileSpreadsheet className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Filters Card */}
      <div className="bg-[#11141D] border border-white/5 rounded-xl p-3 sm:p-4 space-y-3">
          {/* Row 1: Search + Date (compact) + Refresh */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
            <div className="w-full sm:w-64 flex items-center gap-2 bg-[#0B0D12] border border-white/10 rounded-md px-3 h-9 focus-within:ring-1 focus-within:ring-blue-500/50 transition shrink-0">
              <Search className="h-3.5 w-3.5 text-slate-500 shrink-0" />
              <input
                type="text"
                placeholder="Search by caller number..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="flex-1 bg-transparent text-sm text-white placeholder:text-slate-500 focus:outline-none"
                data-testid="search-input"
              />
            </div>
            <div className={`flex-1 min-w-0 ${debouncedSearch ? 'opacity-40 pointer-events-none' : ''}`}>
              <DateFilter onFilterChange={handleDateFilterChange} defaultPreset="today" compact showTimezone={false} />
            </div>
            {debouncedSearch && (
              <span className="text-xs text-blue-400 whitespace-nowrap">Searching all dates</span>
            )}
            <button
              onClick={fetchCalls}
              disabled={loading}
              data-testid="refresh-calls"
              className="flex items-center justify-center gap-1.5 text-slate-400 hover:text-white hover:bg-white/5 rounded-lg px-2.5 py-1.5 transition-all shrink-0"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
              <span className="text-sm">Refresh</span>
            </button>
          </div>

          {/* Divider */}
          <div className="border-t border-white/5" />

          {/* Row 2: Category Pills */}
          <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide">
              {[
                { key: "all", label: "All", icon: null, activeClass: "bg-blue-600/15 border-blue-500/30 text-blue-400" },
                { key: "valid_service_call", label: "Valid", icon: CheckCircle, activeClass: "bg-emerald-600/15 border-emerald-500/30 text-emerald-400" },
                { key: "fake", label: "Fake", icon: AlertTriangle, activeClass: "bg-red-600/15 border-red-500/30 text-red-400" },
                { key: "non_us_accent", label: "Non-US", icon: Globe, activeClass: "bg-orange-600/15 border-orange-500/30 text-orange-400" },
                { key: "ivr_call", label: "IVR/Robot", icon: Bot, activeClass: "bg-blue-600/15 border-blue-500/30 text-blue-400" },
                { key: "adult_call", label: "Adult", icon: Ban, activeClass: "bg-purple-600/15 border-purple-500/30 text-purple-400" },
                { key: "buy_sell_intent", label: "Buy/Sell", icon: ShoppingCart, activeClass: "bg-cyan-600/15 border-cyan-500/30 text-cyan-400" },
                { key: "blacklisted_product", label: "Blacklisted", icon: Ban, activeClass: "bg-rose-600/15 border-rose-500/30 text-rose-400" },
                { key: "pending", label: "Pending", icon: Clock, activeClass: "bg-yellow-600/15 border-yellow-500/30 text-yellow-400" },
                { key: "skipped_call", label: "Skipped", icon: SkipForward, activeClass: "bg-slate-600/15 border-slate-500/30 text-slate-300" },
                { key: "behavioral_spam", label: "Behavioral Spam", icon: ShieldAlert, activeClass: "bg-rose-600/15 border-rose-500/30 text-rose-400" },
              ].map(({ key, label, icon: Icon, activeClass }) => {
                const active = categoryFilter === key;
                const count = categoryCounts[key] ?? 0;
                return (
                  <button
                    key={key}
                    onClick={() => setCategoryFilter(key)}
                    data-testid={`filter-pill-${key}`}
                    className={cn(
                      "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all border whitespace-nowrap shrink-0",
                      active ? activeClass : "border-white/5 bg-[#0B0D12] hover:bg-white/5 text-slate-400"
                    )}
                  >
                    {Icon && <Icon className="h-3 w-3" />}
                    {label}
                    <span
                      className={cn(
                        "ml-0.5 inline-flex items-center justify-center rounded-full px-1.5 h-4 min-w-[1rem] text-[10px] font-semibold tabular-nums",
                        active ? "bg-white/15 text-white" : "bg-white/5 text-slate-500"
                      )}
                      data-testid={`filter-pill-count-${key}`}
                    >
                      {count}
                    </span>
                  </button>
                );
              })}

              <div className="w-px h-5 bg-white/10 mx-1 shrink-0" />

              <Select value={publisherFilter} onValueChange={setPublisherFilter}>
                <SelectTrigger className="w-44 h-8 text-xs shrink-0" data-testid="publisher-filter">
                  <SelectValue placeholder="All Publishers" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Publishers</SelectItem>
                  {publishers.map((pub) => (
                    <SelectItem key={pub} value={pub}>{pub}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={manualValidFilter} onValueChange={setManualValidFilter}>
                <SelectTrigger className="w-40 h-8 text-xs shrink-0" data-testid="manual-valid-filter">
                  <SelectValue placeholder="Manual Valid" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Calls</SelectItem>
                  <SelectItem value="yes">Manual Valid Only</SelectItem>
                </SelectContent>
              </Select>
              
              {activeFilters > 0 && (
                <button
                  onClick={clearAllFilters}
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-medium text-slate-400 hover:text-white hover:bg-white/5 transition-all shrink-0"
                >
                  <X className="h-3 w-3" />
                  Clear ({activeFilters})
                </button>
              )}
          </div>
        </div>

      {/* Calls Table (or Behavioral Spam view when that pill is active) */}
      {categoryFilter === "behavioral_spam" ? (
        <BehavioralSpamView
          startDate={dateFilter?.startDate || ""}
          endDate={dateFilter?.endDate || ""}
        />
      ) : (
      <div className="bg-[#11141D] border border-white/5 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <Table className="min-w-[800px]">
            <TableHeader>
              <TableRow className="border-b border-white/5">
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Caller ID</TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Publisher Name</TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Offer Name</TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500 hidden lg:table-cell" data-testid="buyer-column-header">Buyer</TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Call Duration</TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Call Timestamp (EST)</TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Classification</TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Confidence</TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8">
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
                    </div>
                  </TableCell>
                </TableRow>
              ) : calls.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                    No calls found for today. Calls will appear after Ringba import.
                  </TableCell>
                </TableRow>
              ) : (
                paginatedCalls.map((call) => {
                  const config = categoryConfig[call.category] || categoryConfig.pending;
                  return (
                    <TableRow key={call.id} data-testid={`call-row-${call.id}`} className={cn("border-b border-white/5 hover:bg-white/[0.02] transition-colors", call.manual_valid && "bg-emerald-500/[0.04]")}>
                      <TableCell>
                        <button
                          onClick={() => openCallDetail(call)}
                          className="font-mono text-sm text-blue-500 hover:text-blue-400 hover:underline flex items-center gap-1 cursor-pointer"
                          data-testid={`caller-link-${call.id}`}
                        >
                          {call.caller_number}
                          {call.is_voip && (
                            <span className="ml-1 px-1.5 py-0.5 rounded text-[9px] font-bold bg-amber-500/15 text-amber-400 border border-amber-500/25" title={`VoIP: ${call.phone_lookup?.carrier || "Unknown"}`}>VoIP</span>
                          )}
                          <ExternalLink className="h-3 w-3" />
                        </button>
                      </TableCell>
                      <TableCell className="text-sm">{call.publisher_name || "N/A"}</TableCell>
                      <TableCell className="text-sm max-w-[150px] truncate">
                        {call.offer_name || call.campaign_name || "N/A"}
                      </TableCell>
                      <TableCell className="text-sm max-w-[150px] truncate hidden lg:table-cell" title={call.buyer_name || ""} data-testid={`buyer-cell-${call.id}`}>
                        {call.buyer_name ? (
                          <span className="text-slate-200">{call.buyer_name}</span>
                        ) : (
                          <span className="text-slate-500">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm">{formatDuration(call.call_duration)}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatToEST(call.call_timestamp || call.call_date || call.created_at)}
                      </TableCell>
                      <TableCell>
                        {call.manual_valid ? (
                          <Badge className="bg-emerald-500/15 text-emerald-400 border-emerald-500/25" data-testid={`manual-valid-badge-${call.id}`}>
                            <ShieldCheck className="h-3 w-3 mr-1" />
                            Valid (Manual)
                          </Badge>
                        ) : (
                          <Badge variant="outline" className={config.color}>
                            <span className="flex items-center gap-1">
                              {getCategoryIcon(call.category)}
                              {config.label}
                            </span>
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className={`font-semibold ${getConfidenceScoreColor(call.confidence_score)}`}>
                          {call.confidence_score || 0}%
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => toggleManualValid(call.id, call.manual_valid)}
                            className={call.manual_valid ? "text-emerald-400 hover:text-red-400 hover:bg-red-500/10" : "text-muted-foreground hover:text-emerald-400 hover:bg-emerald-500/10"}
                            title={call.manual_valid ? "Remove Valid Mark" : "Mark as Valid"}
                            data-testid={`toggle-valid-${call.id}`}
                          >
                            <ShieldCheck className="h-4 w-4" />
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                disabled={blockingCaller === call.caller_number || call.caller_blocked || call.caller_status === "blocked"}
                                className={
                                  (call.caller_blocked || call.caller_status === "blocked")
                                    ? "text-red-500/70 cursor-not-allowed"
                                    : "text-muted-foreground hover:text-red-400 hover:bg-red-500/10"
                                }
                                title={
                                  (call.caller_blocked || call.caller_status === "blocked")
                                    ? "Already blocked"
                                    : "Block Caller on Ringba"
                                }
                                data-testid={`block-caller-${call.id}`}
                              >
                                <Ban className={`h-4 w-4 ${blockingCaller === call.caller_number ? "animate-pulse" : ""}`} />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent data-testid={`block-caller-dialog-${call.id}`}>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Block this caller on Ringba?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  This will mark <span className="font-semibold text-red-400">{call.caller_number || "the caller"}</span> as blocked in the local database
                                  and push the block to your Ringba blocked-numbers list. All future calls from this number will be rejected by Ringba.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel data-testid={`block-caller-cancel-${call.id}`}>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleBlockCaller(call.caller_number)}
                                  className="bg-red-600 hover:bg-red-700"
                                  data-testid={`block-caller-confirm-${call.id}`}
                                >
                                  Block on Ringba
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openCallDetail(call)}
                            data-testid={`view-call-${call.id}`}
                          >
                            View Report
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleReanalyze(call.id)}
                            disabled={reanalyzing === call.id}
                            data-testid={`reanalyze-${call.id}`}
                          >
                            <RefreshCw className={`h-4 w-4 ${reanalyzing === call.id ? "animate-spin" : ""}`} />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </div>
      )}

      {/* Pagination Controls */}
      {!loading && categoryFilter !== "behavioral_spam" && <PaginationControls currentPage={currentPage} totalPages={totalPages} totalItems={calls.length} setCurrentPage={setCurrentPage} />}

      {/* Call Analysis Report Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-4xl w-[95vw] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <DialogTitle className="flex items-center gap-2">
                <Phone className="h-5 w-5 text-blue-500" />
                Call Analysis Report
              </DialogTitle>
              {selectedCall && (
                <div className="flex items-center gap-2 sm:mr-8">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={async () => {
                      try {
                        await axios.post(`${API}/calls/${selectedCall.id}/reanalyze`);
                        toast.success('Re-analysis started. Refresh in a few seconds.');
                        setTimeout(() => fetchCalls(), 5000);
                      } catch (e) {
                        toast.error('Error starting re-analysis');
                      }
                    }}
                    className="text-orange-500 border-orange-500/30 hover:bg-orange-500/10"
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Re-Analyze
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => exportCallReport(selectedCall)}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download Report
                  </Button>
                </div>
              )}
            </div>
          </DialogHeader>
          
          {selectedCall && (
            <div className="space-y-6 mt-4">
              {/* Classification Badge */}
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">AI Classification</span>
                <div className="flex items-center gap-2">
                  <Badge className={categoryConfig[selectedCall.category]?.color || categoryConfig.pending.color}>
                    <span className="flex items-center gap-1">
                      {getCategoryIcon(selectedCall.category)}
                      {categoryConfig[selectedCall.category]?.label || "Pending"}
                    </span>
                  </Badge>
                  <CopyButton text={categoryConfig[selectedCall.category]?.label || "Pending"} label="Classification" />
                </div>
              </div>

              {/* Audio Player */}
              {/* Adult Content Hard Block Evidence */}
              {(selectedCall.category === 'adult_call' || selectedCall.is_adult) && (
                <Card className="bg-red-500/10 border-red-500/30" data-testid="adult-block-evidence">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <Ban className="h-4 w-4 text-red-500" />
                      Adult Content — BLOCKED
                      <Badge className="bg-red-500/20 text-red-400 border-0 ml-2">Hard Block</Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Filter Result</span>
                      <Badge className="bg-red-600/20 text-red-400 border-red-500/30">ADULT (BLOCKED)</Badge>
                    </div>
                    {selectedCall.matched_adult_keyword && (
                      <>
                        <Separator className="bg-red-500/10" />
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">Keyword Detected</span>
                          <span className="font-mono font-bold text-red-400">"{selectedCall.matched_adult_keyword}"</span>
                        </div>
                      </>
                    )}
                    {selectedCall.matched_adult_sentence && (
                      <>
                        <Separator className="bg-red-500/10" />
                        <div>
                          <span className="text-sm text-muted-foreground">Matching Sentence</span>
                          <p className="mt-1 text-sm text-red-300/80 italic bg-red-500/5 rounded px-3 py-2">
                            "{selectedCall.matched_adult_sentence}"
                          </p>
                        </div>
                      </>
                    )}
                    {!selectedCall.matched_adult_keyword && (
                      <>
                        <Separator className="bg-red-500/10" />
                        <div className="text-sm text-muted-foreground">
                          Keyword evidence not available — call was classified by AI analysis.
                        </div>
                      </>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Audio Player */}
              <Card className="bg-muted/30">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <Volume2 className="h-4 w-4 text-blue-500" />
                    Call Recording
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <AudioPlayer recordingUrl={selectedCall.recording_url} />
                </CardContent>
              </Card>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Call Information */}
                <Card className="bg-muted/30">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <Phone className="h-4 w-4 text-blue-500" />
                      Call Information
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground flex items-center gap-2">
                        <Phone className="h-3.5 w-3.5" /> Caller ID
                      </span>
                      <div className="flex items-center gap-1">
                        <span className="font-mono">{selectedCall.caller_number}</span>
                        {selectedCall.is_voip && (
                          <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-amber-500/15 text-amber-400 border border-amber-500/25">VoIP</span>
                        )}
                        <CopyButton text={selectedCall.caller_number} label="Caller ID" />
                      </div>
                    </div>
                    {selectedCall.phone_lookup && (
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-muted-foreground">Carrier / Line Type</span>
                        <span className={selectedCall.is_voip ? "text-amber-400 font-medium" : "text-slate-300"}>
                          {selectedCall.phone_lookup.carrier || "Unknown"} ({selectedCall.phone_lookup.line_type || "unknown"})
                        </span>
                      </div>
                    )}
                    <Separator />
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground flex items-center gap-2">
                        <User className="h-3.5 w-3.5" /> Publisher Name
                      </span>
                      <div className="flex items-center gap-1">
                        <span>{selectedCall.publisher_name || "N/A"}</span>
                        <CopyButton text={selectedCall.publisher_name} label="Publisher" />
                      </div>
                    </div>
                    <Separator />
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground flex items-center gap-2">
                        <Target className="h-3.5 w-3.5" /> Offer Name
                      </span>
                      <div className="flex items-center gap-1">
                        <span className="max-w-[150px] truncate">
                          {selectedCall.offer_name || selectedCall.campaign_name || "N/A"}
                        </span>
                        <CopyButton text={selectedCall.offer_name || selectedCall.campaign_name} label="Offer" />
                      </div>
                    </div>
                    <Separator />
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground flex items-center gap-2">
                        <Target className="h-3.5 w-3.5" /> Buyer
                      </span>
                      <div className="flex items-center gap-1">
                        <span className="max-w-[180px] truncate" data-testid="call-detail-buyer">
                          {selectedCall.buyer_name || <span className="text-slate-500">None</span>}
                        </span>
                        {selectedCall.buyer_name && (
                          <CopyButton text={selectedCall.buyer_name} label="Buyer" />
                        )}
                      </div>
                    </div>
                    <Separator />
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground flex items-center gap-2">
                        <Building className="h-3.5 w-3.5" /> DID Number
                      </span>
                      <div className="flex items-center gap-1">
                        <span>{selectedCall.did_number || selectedCall.inbound_number || "N/A"}</span>
                        <CopyButton text={selectedCall.did_number || selectedCall.inbound_number} label="DID" />
                      </div>
                    </div>
                    <Separator />
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground flex items-center gap-2">
                        <Calendar className="h-3.5 w-3.5" /> Date & Time
                      </span>
                      <span>{formatToEST(selectedCall.call_timestamp)}</span>
                    </div>
                    <Separator />
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground flex items-center gap-2">
                        <Clock className="h-3.5 w-3.5" /> Call Duration
                      </span>
                      <span>{formatDuration(selectedCall.call_duration)}</span>
                    </div>
                    {selectedCall.recording_duration_est != null && selectedCall.call_duration > 60 && selectedCall.recording_duration_est < selectedCall.call_duration * 0.3 && (
                      <>
                        <div className="flex items-center justify-between text-amber-400 bg-amber-500/10 rounded px-2 py-1.5" data-testid="recording-mismatch-warning">
                          <span className="flex items-center gap-2 text-xs">
                            <AlertTriangle className="h-3.5 w-3.5" /> Recording only ~{selectedCall.recording_duration_est}s
                          </span>
                          <span className="text-xs opacity-80">Ringba recording incomplete</span>
                        </div>
                      </>
                    )}
                    <Separator />
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground flex items-center gap-2">
                        <Calendar className="h-3.5 w-3.5" /> Appointment Status
                      </span>
                      <Badge className={`text-xs ${
                        selectedCall.appointment_status === 'scheduled' ? 'bg-emerald-500/20 text-emerald-400' :
                        selectedCall.appointment_status === 'attempted' ? 'bg-yellow-500/20 text-yellow-400' :
                        selectedCall.appointment_status === 'callback_requested' ? 'bg-blue-500/20 text-blue-400' :
                        selectedCall.appointment_status === 'declined' ? 'bg-red-500/20 text-red-400' :
                        'bg-slate-500/20 text-slate-400'
                      } border-0`}>
                        {selectedCall.appointment_status === 'scheduled' ? 'Scheduled' :
                         selectedCall.appointment_status === 'attempted' ? 'Attempted' :
                         selectedCall.appointment_status === 'callback_requested' ? 'Callback Requested' :
                         selectedCall.appointment_status === 'declined' ? 'Declined' :
                         'Not Discussed'}
                      </Badge>
                    </div>
                    {selectedCall.appointment_details && (
                      <>
                        <Separator />
                        <div className="flex items-center justify-between">
                          <span className="text-muted-foreground flex items-center gap-2">
                            <FileText className="h-3.5 w-3.5" /> Appointment Details
                          </span>
                          <span className="text-sm text-right max-w-[200px]">{selectedCall.appointment_details}</span>
                        </div>
                      </>
                    )}
                  </CardContent>
                </Card>

                {/* AI Analysis */}
                <Card className="bg-muted/30">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <Target className="h-4 w-4 text-purple-500" />
                      AI Analysis
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <span className="text-sm text-muted-foreground">Confidence Score</span>
                      <div className="flex items-center gap-2 mt-1">
                        <div className="flex-1 bg-muted rounded-full h-2">
                          <div
                            className={`h-2 rounded-full ${
                              (selectedCall.confidence_score || 0) >= 80 ? "bg-emerald-500" :
                              (selectedCall.confidence_score || 0) >= 60 ? "bg-blue-500" :
                              (selectedCall.confidence_score || 0) >= 40 ? "bg-yellow-500" : "bg-red-500"
                            }`}
                            style={{ width: `${selectedCall.confidence_score || 0}%` }}
                          />
                        </div>
                        <span className={`font-bold ${getConfidenceScoreColor(selectedCall.confidence_score)}`}>
                          {selectedCall.confidence_score || 0}%
                        </span>
                      </div>
                    </div>
                    <div>
                      <span className="text-sm text-muted-foreground">Appointment Status</span>
                      <div className="mt-1">
                        <Badge variant="outline">{selectedCall.appointment_status || "Not Detected"}</Badge>
                      </div>
                    </div>
                    {selectedCall.keywords && selectedCall.keywords.length > 0 && (
                      <div>
                        <span className="text-sm text-muted-foreground">Keywords (from Caller)</span>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {selectedCall.keywords.map((kw, idx) => (
                            <Badge key={idx} variant="secondary" className="text-xs">{kw}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    {selectedCall.caller_intent && (
                      <div>
                        <span className="text-sm text-muted-foreground">Caller Intent</span>
                        <p className="mt-1 text-sm text-foreground">{selectedCall.caller_intent}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Accent Detection Info — Multi-Model Pipeline Scoring */}
              {(selectedCall.is_non_us || selectedCall.detected_language || (selectedCall.accent_indicators && selectedCall.accent_indicators.length > 0) || selectedCall.accent_score > 0) && (
                <Card className="bg-orange-500/10 border-orange-500/20">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <Globe className="h-4 w-4 text-orange-500" />
                      Accent / Language Detection
                      {selectedCall.is_non_us && (
                        <Badge className="bg-orange-500/20 text-orange-400 border-0 ml-2">Non-US Detected</Badge>
                      )}
                      {selectedCall.accent_confidence && (
                        <Badge className={`border-0 ml-1 text-xs ${
                          selectedCall.accent_confidence === 'high' ? 'bg-red-500/20 text-red-400' :
                          selectedCall.accent_confidence === 'medium' ? 'bg-yellow-500/20 text-yellow-400' :
                          'bg-slate-500/20 text-slate-400'
                        }`}>
                          {selectedCall.accent_confidence === 'high' ? 'High Confidence' :
                           selectedCall.accent_confidence === 'medium' ? 'Manual Review' : 'Low Confidence'}
                        </Badge>
                      )}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {/* Overall Accent Score */}
                    {selectedCall.accent_score > 0 && (
                      <div>
                        <span className="text-sm text-muted-foreground">Overall Accent Score</span>
                        <div className="flex items-center gap-3 mt-1">
                          <div className="flex-1 bg-white/5 rounded-full h-2.5 overflow-hidden">
                            <div 
                              className={`h-full rounded-full transition-all ${
                                selectedCall.accent_score >= 80 ? 'bg-red-500' : 
                                selectedCall.accent_score >= 65 ? 'bg-orange-500' : 
                                selectedCall.accent_score >= 50 ? 'bg-yellow-500' : 'bg-green-500'
                              }`}
                              style={{ width: `${Math.min(100, selectedCall.accent_score)}%` }}
                            />
                          </div>
                          <span className="text-sm font-bold text-orange-400">{selectedCall.accent_score}%</span>
                        </div>
                      </div>
                    )}

                    {/* Score Breakdown */}
                    {selectedCall.accent_breakdown && (
                      <div className="grid grid-cols-2 gap-2">
                        {[
                          { label: "Phoneme", score: selectedCall.accent_breakdown.phoneme_score, weight: "35%", desc: "V/W confusion, retroflex T/D" },
                          { label: "Prosody", score: selectedCall.accent_breakdown.prosody_score, weight: "25%", desc: "Pitch, stress, rhythm" },
                          { label: "Fake Accent", score: selectedCall.accent_breakdown.fake_accent_score, weight: "20%", desc: "Accent shifts, scripted" },
                          { label: "Linguistic", score: selectedCall.accent_breakdown.linguistic_score, weight: "10%", desc: "Phrasing patterns" },
                        ].filter(s => s.score > 0).map((s, idx) => (
                          <div key={idx} className="bg-white/[0.03] rounded-lg p-2">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-xs text-slate-400">{s.label} <span className="text-slate-600">({s.weight})</span></span>
                              <span className={`text-xs font-bold ${s.score >= 70 ? 'text-red-400' : s.score >= 40 ? 'text-orange-400' : 'text-green-400'}`}>{s.score}</span>
                            </div>
                            <div className="bg-white/5 rounded-full h-1.5 overflow-hidden">
                              <div className={`h-full rounded-full ${s.score >= 70 ? 'bg-red-500' : s.score >= 40 ? 'bg-orange-500' : 'bg-green-500'}`}
                                style={{ width: `${s.score}%` }} />
                            </div>
                            <span className="text-[10px] text-slate-600 mt-0.5 block">{s.desc}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {selectedCall.detected_language && selectedCall.detected_language !== 'en' && (
                      <div>
                        <span className="text-sm text-muted-foreground">Detected Language</span>
                        <p className="mt-1 text-sm font-medium text-orange-400">
                          {selectedCall.detected_language === 'hi' ? 'Hindi' :
                           selectedCall.detected_language === 'ur' ? 'Urdu' :
                           selectedCall.detected_language === 'tl' ? 'Tagalog' :
                           selectedCall.detected_language === 'es' ? 'Spanish' :
                           selectedCall.detected_language === 'zh' ? 'Chinese' :
                           selectedCall.detected_language.toUpperCase()}
                        </p>
                      </div>
                    )}
                    {selectedCall.accent_indicators && selectedCall.accent_indicators.length > 0 && (
                      <div>
                        <span className="text-sm text-muted-foreground">Accent Indicators Found</span>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {selectedCall.accent_indicators.slice(0, 12).map((indicator, idx) => (
                            <Badge key={idx} className="text-xs bg-orange-500/20 text-orange-400 border-0">
                              {indicator}
                            </Badge>
                          ))}
                          {selectedCall.accent_indicators.length > 12 && (
                            <Badge className="text-xs bg-orange-500/10 text-orange-500 border-0">
                              +{selectedCall.accent_indicators.length - 12} more
                            </Badge>
                          )}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* IVR/Robot Detection Info */}
              {(selectedCall.is_ivr || selectedCall.category === 'ivr_call' || (selectedCall.ivr_indicators && selectedCall.ivr_indicators.length > 0)) && (
                <Card className="bg-blue-500/10 border-blue-500/20">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <Bot className="h-4 w-4 text-blue-500" />
                      IVR / Robotic Call Detection
                      <Badge className="bg-blue-500/20 text-blue-400 border-0 ml-2">Automated Caller Detected</Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div>
                      <span className="text-sm text-muted-foreground">This caller appears to be an automated system or robotic voice</span>
                    </div>
                    {selectedCall.ivr_indicators && selectedCall.ivr_indicators.length > 0 && (
                      <div>
                        <span className="text-sm text-muted-foreground">IVR Indicators Found</span>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {selectedCall.ivr_indicators.map((indicator, idx) => (
                            <Badge key={idx} className="text-xs bg-blue-500/20 text-blue-400 border-0">
                              {indicator}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Buy/Sell Intent Analysis */}
              {(selectedCall.is_buy_sell || selectedCall.category === 'buy_sell_intent' || (selectedCall.buy_sell_details && typeof selectedCall.buy_sell_details === 'object')) && (
                <Card className="bg-cyan-500/10 border-cyan-500/20">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium flex items-center gap-2" data-testid="buy-sell-details-title">
                      <ShoppingCart className="h-4 w-4 text-cyan-500" />
                      Buy/Sell Intent Analysis
                      {selectedCall.category === 'buy_sell_intent' && (
                        <Badge className="bg-cyan-500/20 text-cyan-400 border-0 ml-2">Product Intent Detected</Badge>
                      )}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {selectedCall.buy_sell_details && typeof selectedCall.buy_sell_details === 'object' && (
                      <>
                        {selectedCall.buy_sell_details.buy_sell_type && (
                          <div>
                            <span className="text-sm text-muted-foreground">Intent Type</span>
                            <p className="mt-1 text-sm font-medium text-cyan-400 capitalize">
                              {selectedCall.buy_sell_details.buy_sell_type.replace(/_/g, ' ')}
                            </p>
                          </div>
                        )}
                        {selectedCall.buy_sell_details.buy_sell_confidence > 0 && (
                          <div>
                            <span className="text-sm text-muted-foreground">Classification Confidence</span>
                            <div className="flex items-center gap-3 mt-1">
                              <div className="flex-1 bg-white/5 rounded-full h-2 overflow-hidden">
                                <div className={`h-full rounded-full ${
                                  selectedCall.buy_sell_details.buy_sell_confidence >= 70 ? 'bg-cyan-500' : 'bg-yellow-500'
                                }`} style={{ width: `${selectedCall.buy_sell_details.buy_sell_confidence}%` }} />
                              </div>
                              <span className="text-sm font-bold text-cyan-400">{selectedCall.buy_sell_details.buy_sell_confidence}%</span>
                            </div>
                          </div>
                        )}
                        {selectedCall.buy_sell_details.conversation_evolution && (
                          <div>
                            <span className="text-sm text-muted-foreground">Conversation Evolution</span>
                            <p className="mt-1 text-sm text-slate-300">{selectedCall.buy_sell_details.conversation_evolution}</p>
                          </div>
                        )}
                        {selectedCall.buy_sell_details.key_sentences && selectedCall.buy_sell_details.key_sentences.length > 0 && (
                          <div>
                            <span className="text-sm text-muted-foreground">Key Sentences</span>
                            <div className="mt-1 space-y-1">
                              {selectedCall.buy_sell_details.key_sentences.slice(0, 5).map((s, idx) => (
                                <p key={idx} className="text-xs text-slate-400 italic bg-white/[0.03] rounded px-2 py-1">"{s}"</p>
                              ))}
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Summary */}
              <Card className="bg-muted/30">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <FileText className="h-4 w-4 text-green-500" />
                      Caller's Request Summary
                    </CardTitle>
                    <CopyButton text={selectedCall.summary} label="Summary" />
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm leading-relaxed">
                    {selectedCall.summary || "No summary available. AI analysis may be in progress."}
                  </p>
                </CardContent>
              </Card>

              {/* Transcript */}
              <Card className="bg-muted/30">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <MessageSquare className="h-4 w-4 text-blue-500" />
                      Transcript
                    </CardTitle>
                    <CopyButton text={selectedCall.transcript} label="Transcript" />
                  </div>
                </CardHeader>
                <CardContent>
                  {selectedCall.transcript ? (
                    <div className="bg-background rounded-lg p-4 max-h-[300px] overflow-y-auto">
                      <p className="text-sm whitespace-pre-wrap font-mono leading-relaxed">
                        {selectedCall.transcript}
                      </p>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <MessageSquare className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p>No transcript available</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
